// ── Firebase Storage helpers ─────────────────────────────────────────────────
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

// Limites de tamanho de upload por categoria. Pra imagens, isso é checado
// DEPOIS da compressão (compressImageIfNeeded já reduz pra ~700 KB) — então
// na prática só barra formatos que o browser não consegue decodificar em
// canvas (ex: HEIC), não fotos grandes normais. 15 MB de margem cobre até
// esses casos raros sem rejeitar arquivo à toa.
export const UPLOAD_LIMITS = {
  image: 15 * 1024 * 1024, // 15 MB — fotos do casal, galeria, cards
  document: 6 * 1024 * 1024, // 6 MB — RG, comprovantes pra Asaas (sem compressão agressiva)
  generic: 15 * 1024 * 1024,
};

export class UploadTooLargeError extends Error {
  constructor(file, limit) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const limitMB = Math.round(limit / (1024 * 1024));
    super(`Arquivo muito grande (${sizeMB} MB, limite ${limitMB} MB). Tente enviar uma foto menor ou em outro formato (JPEG costuma funcionar melhor).`);
    this.name = 'UploadTooLargeError';
    this.fileSize = file.size;
    this.limit = limit;
  }
}

export function checkUploadSize(file, kind = 'image') {
  if (!file) return;
  const limit = UPLOAD_LIMITS[kind] ?? UPLOAD_LIMITS.generic;
  if (file.size > limit) throw new UploadTooLargeError(file, limit);
}

export class UnsupportedImageFormatError extends Error {
  constructor() {
    super('Não conseguimos abrir esse arquivo de imagem. Tente salvar como JPEG ou PNG e enviar de novo.');
    this.name = 'UnsupportedImageFormatError';
  }
}

// Formatos que recomprimimos ativamente (resize + reencode JPEG).
const RECOMPRESS_TYPES = ['image/jpeg', 'image/jpg', 'image/webp', 'image/png'];

async function compressImageIfNeeded(file) {
  if (!(file instanceof File)) return file;
  if (!file.type.startsWith('image/')) return file;

  const needsRecompress = RECOMPRESS_TYPES.includes(file.type) && file.size > 700 * 1024;

  const MAX_DIM = 1600;
  const QUALITY = 0.82;

  const srcUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = srcUrl;
    });

    // Formato desconhecido (ex: HEIC) ou arquivo pequeno de um formato já
    // conhecido — não precisa recomprimir, mas o decode acima (new Image())
    // já rodou e confirmou que o arquivo não está truncado/corrompido. Sem
    // essa checagem, um upload cortado no meio (ex: conexão instável em
    // WebView Android) passava direto e só quebrava depois, na geração de IA.
    if (!needsRecompress) return file;

    const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob) return file;
    if (blob.size >= file.size) return file;

    const safeName = file.name.replace(/\.[^.]+$/, '') || 'upload';
    return new File([blob], `${safeName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    // O mesmo decodificador que usamos aqui (canvas) é o que o navegador
    // usa pra exibir a foto depois — se não conseguiu abrir agora, também
    // não vai conseguir mostrar a foto na página. Melhor avisar já do que
    // subir um arquivo que nunca vai aparecer pra ninguém.
    throw new UnsupportedImageFormatError();
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
}

/**
 * Upload a File to Firebase Storage.
 * @param {File}     file         - The file to upload
 * @param {string}   path         - Storage path, e.g. 'weddings/uid/cover.jpg'
 * @param {Function} onProgress   - Optional progress callback (0-100)
 * @returns {Promise<string>}     - Download URL
 */
export function uploadFile(file, path, onProgress) {
  // Documentos (paths que contém /documents/ ou /docs/ ou começam com
  // submitRecipientDocument) usam o limite menor (6 MB).
  const kind = /docs?\//i.test(String(path || '')) ? 'document' : 'image';
  // Comprime ANTES de checar o tamanho — assim uma foto de 27 MB que vira
  // ~500 KB depois de reduzida nunca é rejeitada por causa do tamanho
  // original. O limite só barra o que sobra grande mesmo depois de tentar
  // comprimir (documento, formato que o canvas não decodifica, etc.).
  return compressImageIfNeeded(file).then((optimized) => {
    checkUploadSize(optimized, kind);
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, optimized, {
        contentType: optimized.type || file.type,
      });

      task.on(
        'state_changed',
        snapshot => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress?.(pct);
        },
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        }
      );
    });
  });
}

/**
 * Delete a file from Firebase Storage by its full URL.
 */
export async function deleteFileByUrl(url) {
  try {
    const fileRef = ref(storage, url);
    await deleteObject(fileRef);
  } catch {
    // ignore if already deleted
  }
}

/**
 * Build a deterministic storage path for a user asset.
 * Examples:
 *   assetPath('abc123', 'cards', 'card-xyz.jpg')
 *   → 'weddings/abc123/cards/card-xyz.jpg'
 */
export function assetPath(userId, ...segments) {
  return ['weddings', userId, ...segments].join('/');
}
